import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { MobileEventSelectorWBH } from "@/components/rider/MobileEventSelectorWBH";
import { DesktopEventSelectorWBH } from "@/components/rider/desktop/DesktopEventSelectorWBH";
import { LoadingEvents } from "@/components/rider/LoadingEvents";
import { CommunityEvent } from "@/types/rider";
import { getEventById } from "@/services/riderService";

export interface WBHVehicleOption {
  id: string;
  name: string;
  capacity: number;
  price: number;
  isRoundTrip: boolean;
  durationHours: number;
  icon: string;
}

export type DaySelection = 'friday' | 'saturday' | 'sunday';

// Default vehicle options based on bus_pricing_info
const defaultVehicleOptions: WBHVehicleOption[] = [
  { id: 'shuttle_single_day', name: 'WBH Single Day Shuttle Pass', capacity: 1, price: 30, isRoundTrip: true, durationHours: 1, icon: '🚐' },
  { id: 'shuttle_3_day', name: 'WBH 3-Day Shuttle Pass', capacity: 1, price: 75, isRoundTrip: true, durationHours: 1, icon: '🚐' },
  { id: 'party_bus_one_way_private', name: 'One Way Private Party Bus', capacity: 25, price: 1000, isRoundTrip: false, durationHours: 2, icon: '🚌' },
  { id: 'party_bus_all_day_private', name: 'All Day Private Party Bus', capacity: 25, price: 2200, isRoundTrip: true, durationHours: 7, icon: '🚌' },
  { id: 'party_bus1', name: 'Party Bus One Way', capacity: 30, price: 1050, isRoundTrip: false, durationHours: 3, icon: '🚌' }
];

const RiderEventSelectWeBelongHereMiami = () => {
  const [event, setEvent] = useState<CommunityEvent | null>(null);
  const [vehicleOptions, setVehicleOptions] = useState<WBHVehicleOption[]>(defaultVehicleOptions);
  const [selectedDay, setSelectedDay] = useState<DaySelection>('friday');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventSelected, setEventSelected] = useState(false);
  const navigate = useNavigate();
  const [eventImage, setEventImage] = useState<string>("/Default_Party_Bus.jpeg");

  // We Belong Here Miami event ID from the database
  const WE_BELONG_HERE_MIAMI_EVENT_ID = "d42a1807-432b-4dc9-877f-1a06d72b0794";

  // Day to date mapping for We Belong Here Miami 2026
  const dayDates: Record<DaySelection, string> = {
    friday: 'Friday, Feb 27, 2026',
    saturday: 'Saturday, Feb 28, 2026',
    sunday: 'Sunday, Mar 1, 2026'
  };

  // Helper function to convert relative image paths to absolute URLs
  const getAbsoluteImageUrl = (imagePath: string) => {
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return `${window.location.origin}${imagePath}`;
  };

  useEffect(() => {
    const loadEventData = async () => {
      try {
        console.log("Loading We Belong Here Miami event with ID:", WE_BELONG_HERE_MIAMI_EVENT_ID);
        const eventData = await getEventById(WE_BELONG_HERE_MIAMI_EVENT_ID);
        
        if (eventData) {
          console.log("Received event data:", eventData);
          
          // Set single event with custom multi-day date display
          setEvent({
            ...eventData,
            name: eventData.name || "We Belong Here Miami",
            location: eventData.location || "Miami, FL",
            region: "Miami",
            date: "Friday Feb 27, Saturday Feb 28, Sunday Mar 1"
          });
          
          // Transform bus_pricing_info into vehicle options if available
          if (eventData.bus_pricing_info) {
            const pricingInfo = eventData.bus_pricing_info;
            const transformedOptions: WBHVehicleOption[] = Object.entries(pricingInfo).map(([key, value]: [string, any]) => ({
              id: key,
              name: value.user_friendly_name || key,
              capacity: value.passenger_count || 1,
              price: value.rider_price || 0,
              isRoundTrip: value.is_round_trip || false,
              durationHours: value.duration_hours || 1,
              icon: key.includes('shuttle') ? '🚐' : '🚌'
            }));
            
            if (transformedOptions.length > 0) {
              setVehicleOptions(transformedOptions);
            }
          }
          
          // Set the event image for meta tags
          if (eventData.image) {
            setEventImage(eventData.image);
          }
        } else {
          // Fallback event data
          setEvent({
            id: WE_BELONG_HERE_MIAMI_EVENT_ID,
            name: "We Belong Here Miami",
            location: "Miami, FL",
            region: "Miami",
            image: "/Default_Party_Bus.jpeg",
            date: "Friday Feb 27, Saturday Feb 28, Sunday Mar 1",
            startTime: "TBD",
            price: 30
          });
        }
      } catch (error) {
        console.error("Failed to load We Belong Here Miami event:", error);
        // Fallback event data
        setEvent({
          id: WE_BELONG_HERE_MIAMI_EVENT_ID,
          name: "We Belong Here Miami",
          location: "Miami, FL",
          region: "Miami",
          image: "/Default_Party_Bus.jpeg",
          date: "Friday Feb 27, Saturday Feb 28, Sunday Mar 1",
          startTime: "TBD",
          price: 30
        });
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, []);

  const handleEventSelect = () => {
    setEventSelected(true);
  };

  const handleBackToEvent = () => {
    setEventSelected(false);
    setSelectedVehicleId(null);
  };

  const handleDaySelect = (day: DaySelection) => {
    setSelectedDay(day);
  };

  const handleVehicleSelection = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
  };

  const handleContinue = () => {
    if (selectedVehicleId && event) {
      const selectedVehicle = vehicleOptions.find(v => v.id === selectedVehicleId);
      if (!selectedVehicle) return;

      const venueAddress = event.location || "We Belong Here Miami, Miami, FL";
      
      const params = new URLSearchParams({ 
        eventId: WE_BELONG_HERE_MIAMI_EVENT_ID,
        capacity: selectedVehicle.capacity.toString(),
        isRoundTrip: selectedVehicle.isRoundTrip.toString(),
        destination: venueAddress,
        vehicleType: selectedVehicle.name,
        pricingKey: selectedVehicle.id,
        selectedDay: selectedDay,
        price: selectedVehicle.price.toString(),
        durationHours: selectedVehicle.durationHours.toString()
      });
      
      // Create notes with vehicle information
      const notes = `${selectedVehicle.name} - Capacity ${selectedVehicle.capacity} - ${selectedVehicle.isRoundTrip ? 'Round Trip' : 'One Way'} - ${selectedVehicle.durationHours}hr - ${dayDates[selectedDay]}`;
      params.append('notes', notes);
      params.append('returnPath', '/we-belong-here-miami');
      
      // Day-specific time presets for one-way trips
      const timePresets = selectedDay === 'sunday' 
        ? [
            { label: "Early", time: "15:00", description: "3:00 PM pickup" },
            { label: "Sunset", time: "18:00", description: "6:00 PM pickup" },
            { label: "Ride Back", time: "11:30", description: "11:30 AM pickup" }
          ]
        : [
            { label: "Early", time: "17:00", description: "5:00 PM pickup" },
            { label: "Sunset", time: "20:00", description: "8:00 PM pickup" },
            { label: "Ride Back", time: "01:30", description: "1:30 AM pickup" }
          ];
      params.append('timePresets', JSON.stringify(timePresets));
      
      navigate(`/rider-quote?${params.toString()}`);
    }
  };

  const handleManualEntry = () => {
    navigate('/');
  };

  return (
    <RiderLayout>
      <Helmet>
        <title>We Belong Here Miami | Disco Party Bus</title>
        <meta name="description" content="Book your party bus or shuttle for We Belong Here Miami - Miami's premier music experience" />
        <meta property="og:title" content="We Belong Here Miami | Disco Party Bus" />
        <meta property="og:description" content="Book your party bus or shuttle for We Belong Here Miami - Miami's premier music experience" />
        <meta property="og:image" content={getAbsoluteImageUrl(eventImage)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${window.location.origin}/we-belong-here-miami`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={getAbsoluteImageUrl(eventImage)} />
      </Helmet>
      <div className="min-h-screen bg-white transition-colors duration-300">
        {loading ? (
          <LoadingEvents />
        ) : event ? (
          <>
            <MobileEventSelectorWBH
              event={event}
              eventSelected={eventSelected}
              selectedDay={selectedDay}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              dayDates={dayDates}
              onEventSelect={handleEventSelect}
              onBackToEvent={handleBackToEvent}
              onDaySelect={handleDaySelect}
              onVehicleSelect={handleVehicleSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
            />

            <DesktopEventSelectorWBH
              event={event}
              eventSelected={eventSelected}
              selectedDay={selectedDay}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              dayDates={dayDates}
              onEventSelect={handleEventSelect}
              onBackToEvent={handleBackToEvent}
              onDaySelect={handleDaySelect}
              onVehicleSelect={handleVehicleSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
            />
          </>
        ) : null}
      </div>
    </RiderLayout>
  );
};

export default RiderEventSelectWeBelongHereMiami;